import React from 'react';

import { useBaseTable } from 'hooks/useBaseTable';
import { useBaseWidget } from 'hooks/useBaseWidget';

import { Risk } from 'api/queries/getRiskRegister.query';

import { TooltipPosition } from 'components/common/CustomizedTooltip/customizedTooltip.enums';
import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import Icon from 'components/common/Icons/icon';
import { CustomTableColumnsModal } from 'components/common/Modals/CustomTableColumns/customTableColumnsModal';
import { ShareInsightButton } from 'components/common/ShareInsightButton/ShareInsightButton';
import { TableV8 } from 'components/common/Tables/TableV8/tableV8';
import { Separator } from 'components/common/WidgetWithTitle/Separator/separator';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';
import { CustomPagination } from 'components/common/pagination/customPagination';
import { TitleWithCounter } from 'components/common/titleWithCounter/titleWithCounter';

import { CustomizedRoundIcon, CustomizedTooltip } from 'components/common';
import { useRiskRegisterLogic } from 'components/widgets/RiskRegister/RiskRegister.logic';
import classes from 'components/widgets/RiskRegister/RiskRegister.module.scss';
import { useRiskRegisterProvider } from 'components/widgets/RiskRegister/RiskRegister.provider';
import { columnsV8, initialState } from 'components/widgets/RiskRegister/RiskRegister.utils';
import { CreateRiskRegisterButton } from 'components/widgets/RiskRegister/components/CreateRiskRegisterButton/CreateRiskRegisterButton';
import { DeleteRiskRegisterButton } from 'components/widgets/RiskRegister/components/DeleteRiskRegisterButton/DeleteRiskRegisterButton';
import { ExportToCsvButton } from 'components/widgets/RiskRegister/components/ExportToCsvButton/ExportToCsvButton';
import { Filters } from 'components/widgets/RiskRegister/components/Filters/Filters';
import { RiskRegisterRoutes } from 'components/widgets/RiskRegister/components/RiskRegisterRoutes/RiskRegisterRoutes';
import { RunQsra } from 'components/widgets/RiskRegister/components/RunQsra/RunQsra';
import { UploadRisksButton } from 'components/widgets/RiskRegister/components/UploadRisksButton/UploadRisksButton';

const id = 'riskRegister';
const title = 'Risk Register';
const componentKey = 'riskRegister';

export const RiskRegister = ({
    projectId,
    contractId,
    latestVersionId,
    globalFilterData,
    widgetId,
    localFilters,
    setSubTitleForExport,
    activeTab,
}) => {
    // Table
    const { table } = useBaseTable({
        widgetId,
        localFilters,
        componentKey,
        initialState,
        columns: columnsV8,
    });

    // Provider
    const { ui, api } = useRiskRegisterProvider({
        widgetId,
        contractId,
        projectId,
        sorting: table.sorting,
        globalFilterData,
        latestVersionId,
    });

    // Widget
    const { subTitle, route } = useBaseWidget({
        id,
        widgetId,
        projectId,
        contractId,
        latestVersionId,
        title,
        setSubTitleForExport,
    });

    const { logic } = useRiskRegisterLogic({
        ui,
        api,
        table,
    });

    return activeTab === 'table' ? (
        <WidgetWithTitle
            id={id}
            title={<TitleWithCounter title={title} counter={logic.rowCount} />}
            tooltip={api.riskRegister.data?.info}
            titleFilters={[
                <div className={classes.titleFilters} key={'filters'}>
                    <Filters ui={ui} />
                    <DeleteRiskRegisterButton table={table} />
                </div>,
            ]}
            titleComponents={[
                <RunQsra key={'runQsra'} latestVersionId={latestVersionId} tableRef={table.tableRef} />,
                <CreateRiskRegisterButton key={'create'} />,
                <Separator key={'separator'} className={classes.separator} />,
                <UploadRisksButton key={'uploadRisksButton'} />,
                <CustomizedTooltip
                    key={'tableColumns'}
                    tooltipPosition={TooltipPosition.Top}
                    tooltipContent={'Customize table columns'}
                    triggerElement={
                        <CustomizedRoundIcon enableHover={true} onClick={() => table.setModalColumnsOpen(true)}>
                            <Icon name={'tune-filter'} />
                        </CustomizedRoundIcon>
                    }
                />,
                <ShareInsightButton key={'shareInsight'} title={title} link={route} />,
                <ExportDropdown key={'export'} title={title} subTitle={subTitle}>
                    <ExportToCsvButton
                        headers={logic.exportCsv.headers}
                        rows={logic.exportCsv.rows}
                        fileName={logic.exportCsv.fileName}
                    />
                </ExportDropdown>,
            ]}
        >
            <div className={classes.riskRegister}>
                {table.updatedInitialState && (
                    <>
                        <TableV8<Risk>
                            data={api.riskRegister.data?.riskList || []}
                            columns={columnsV8}
                            isLoading={api.riskRegister.isFetching}
                            sorting={table.sorting}
                            setSorting={table.setSorting}
                            columnVisibility={table.columnVisibility}
                            setColumnVisibility={table.setColumnVisibility}
                            columnOrder={table.columnOrder}
                            setColumnOrder={table.setColumnOrder}
                            manualSorting={false}
                            manualPagination={false}
                            manualFiltering={false}
                            pagination={table.pagination}
                            columnFilters={table.columnFilters}
                            rowSelection={table.rowSelection}
                            setRowSelection={table.setRowSelection}
                            ref={table.tableRef}
                        />
                        <div className={classes.pagination}>
                            <CustomPagination
                                totalPages={table.totalPages}
                                pagination={table.pagination}
                                setPagination={table.setPagination}
                                isDisabled={api.riskRegister.isFetching}
                            />
                        </div>
                    </>
                )}
            </div>
            <CustomTableColumnsModal
                onHideModal={() => table.setModalColumnsOpen(false)}
                showModal={table.modalColumnsOpen}
                widgetId={widgetId}
                componentKey={componentKey}
                tableInstance={table.tableInstance}
                initialState={initialState}
                columns={columnsV8}
                onChangeOrder={table.setColumnOrder}
                onChangeVisibility={table.setColumnVisibility}
            />
            <RiskRegisterRoutes />
        </WidgetWithTitle>
    ) : null;
};
